import { useEffect } from "react";

import { useParams } from "react-router";

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { compact, isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { useLocalizationContext } from "@mui/x-date-pickers/internals";

import type { ResponseError } from "@readai/common/api";
import { toast } from "@readai/common/hooks/useToasts";
import { hasRetryableStatus } from "@readai/common/utils";

import * as api from "@app/booking/api";
import useSchedulingStore from "@app/booking/stores/scheduling";
import type { PublicRssLink } from "@app/booking/types/rssLinkTypes";
import type {
  ApiScheduleRecommendationRequestBody,
  ScheduleRecommendation,
  ScheduleRecommendationBestOptions,
  ScheduleRecommendationDailyAvailability,
} from "@app/booking/types/scheduleRecommendationTypes";

export const BASE_QUERY_KEY = ["booking", "scheduleRecommendations"];
const EMPTY_AVAILABILITY: ScheduleRecommendationDailyAvailability = {};
const EMPTY_TIME_SLOTS: ScheduleRecommendation[] = [];
const EMPTY_BEST_OPTIONS: ScheduleRecommendationBestOptions = {
  thisWeek: [],
  nextWeek: [],
};

const formatDate = (date: DateTime) => date.toUTC().toISO()!;
const inTimezone = (date: DateTime, zone: string | null) =>
  zone ? date.setZone(zone) : date;

const filterValidWindows = (
  recommendationWindows: ApiScheduleRecommendationRequestBody["recommendationWindows"],
) => {
  return recommendationWindows.filter((window) => {
    const start = DateTime.fromISO(window.start);
    const end = DateTime.fromISO(window.end);

    return start.isValid && end.isValid && start < end;
  });
};

const constructBestOptionsBody = ({
  constraints,
  timezone,
  dateUtils,
}: {
  constraints: PublicRssLink | null;
  timezone: string | null;
  dateUtils: {
    startOfWeek: (date: DateTime) => DateTime;
    endOfWeek: (date: DateTime) => DateTime;
  };
}) => {
  if (!constraints) return null;

  const now = inTimezone(DateTime.now(), timezone);
  const windowEndTime = inTimezone(constraints.windowEndTime, timezone);

  // we'll ask for available time slots for the whole period, and the BE will enforce
  // restricting to the owner's working hours as applicable
  const startOfThisWeek = dateUtils.startOfWeek(now);
  const endOfThisWeek = dateUtils.endOfWeek(now);

  const startOfNextWeek = startOfThisWeek.plus({ week: 1 });
  const endOfNextWeek = endOfThisWeek.plus({ week: 1 });

  const includeNextWeek =
    startOfThisWeek < windowEndTime &&
    constraints.windowStartTime <= endOfNextWeek;

  const earliestAvailability = now.plus({ minutes: 5 });
  const earliestAvailabilityWithBuffer = now.plus({
    hours: constraints.minimumSchedulingBuffer,
  });
  const earliestAvailableStartTime = DateTime.max(
    earliestAvailability,
    startOfThisWeek,
    earliestAvailabilityWithBuffer,
  );

  const recommendationWindows: ApiScheduleRecommendationRequestBody["recommendationWindows"] =
    compact([
      {
        start: formatDate(earliestAvailableStartTime),
        end: formatDate(DateTime.min(endOfThisWeek, windowEndTime)),
        label: "thisWeek",
      },
      includeNextWeek
        ? {
            start: formatDate(startOfNextWeek),
            end: formatDate(DateTime.min(endOfNextWeek, windowEndTime)),
            label: "nextWeek",
          }
        : null,
    ]);

  const body: ApiScheduleRecommendationRequestBody = {
    proposed: {
      timeRange: {
        start: formatDate(earliestAvailableStartTime),
        end: formatDate(
          DateTime.min(
            earliestAvailableStartTime.plus({
              minutes: constraints.allowedDuration,
            }),
            windowEndTime,
          ),
        ),
        tzName: timezone ?? undefined,
      },
    },
    recommendationWindows: filterValidWindows(recommendationWindows),
  };

  return body;
};

const constructScheduleRecommendationsBodyForPeriod = ({
  constraints,
  periodStart,
  periodEnd,
  timezone,
}: {
  constraints: PublicRssLink;
  periodStart: DateTime;
  periodEnd: DateTime;
  timezone: string | null;
}) => {
  // request the start of the selected period if possible, otherwise only start as
  // early as the link's window start time allows, with giving *at minimum* a 5-min
  // buffer
  const now = inTimezone(DateTime.now(), timezone);
  const earliestAvailability = now.plus({ minutes: 5 });
  const earliestAvailabilityWithBuffer = now.plus({
    hours: constraints.minimumSchedulingBuffer,
  });
  const earliestAvailableStartTime = DateTime.max(
    earliestAvailability,
    earliestAvailabilityWithBuffer,
    periodStart,
  );

  // small buffer to the window end time. the round trip from the original link config
  // to this request body being generated/sent up to the BE is just long enough that
  // the BE will throw an error saying the window end is outside the bounds of the link,
  // even if it's just a matter of seconds
  const windowEndTime = inTimezone(constraints.windowEndTime, timezone).minus({
    minute: 1,
  });

  // ensure that the end time does not exceed the window end time
  const proposedEndTime = DateTime.min(
    earliestAvailableStartTime.plus({
      minutes: constraints.allowedDuration,
    }),
    windowEndTime,
  );

  const latestRecommendationWindowEndTime = DateTime.min(
    periodEnd,
    windowEndTime,
  );

  const body: ApiScheduleRecommendationRequestBody = {
    proposed: {
      timeRange: {
        start: formatDate(earliestAvailableStartTime),
        end: formatDate(proposedEndTime),
        tzName: timezone ?? undefined,
      },
    },
    recommendationWindows: filterValidWindows([
      {
        start: formatDate(earliestAvailableStartTime),
        end: formatDate(latestRecommendationWindowEndTime),
      },
    ]),
  };

  return body;
};

const constructScheduleRecommendationsBodyForDay = ({
  constraints,
  date,
  timezone,
}: {
  date: DateTime | null;
  constraints: PublicRssLink | null;
  timezone: string | null;
}) => {
  if (!date || !constraints) return null;

  const timezoneAwareDate = inTimezone(date, timezone);
  const periodStart = timezoneAwareDate.startOf("day");
  const periodEnd = timezoneAwareDate.endOf("day");

  return constructScheduleRecommendationsBodyForPeriod({
    constraints,
    periodStart,
    periodEnd,
    timezone,
  });
};

const constructScheduleRecommendationsBodyForMonth = ({
  constraints,
  date,
  timezone,
}: {
  date: DateTime | null;
  constraints: PublicRssLink | null;
  timezone: string | null;
}) => {
  if (!date || !constraints) return null;

  const timezoneAwareDate = inTimezone(date, timezone);

  // the calendar view will always display 6 weeks of data. based on which weekday the current
  // month starts on, almost the entire last week of the previous month could be visible, so give
  // a 1-week buffer on the period start
  const periodStart = timezoneAwareDate.startOf("month").minus({ week: 1 });

  // similarly, up to 2 weeks of the next month could be visible (e.g., if viewing February,
  // and it starts on a Monday), so give 2 weeks buffer to the end of the month
  const periodEnd = timezoneAwareDate.endOf("month").plus({ week: 2 });

  return constructScheduleRecommendationsBodyForPeriod({
    constraints,
    periodStart,
    periodEnd,
    timezone,
  });
};

const useAvailability = () => {
  const { ownerVanityId, linkId: linkIdParam } = useParams();

  const localizationContext = useLocalizationContext<DateTime>();

  const { constraints, selectedDate, selectedMonth, selectedTimezone } =
    useSchedulingStore((state) => ({
      constraints: state.constraints,
      selectedDate: state.selectedDate,
      selectedMonth: state.selectedMonth,
      selectedTimezone: state.selectedTimezone,
    }));

  const linkId = linkIdParam ?? constraints?.vanityId;
  const queriesEnabled = Boolean(ownerVanityId) && Boolean(linkId);

  const rootQueryKey = [
    ...BASE_QUERY_KEY,
    ownerVanityId,
    linkId,
    JSON.stringify(constraints),
    selectedTimezone,
  ];

  const scheduleRecommendationsForDayQuery = useQuery({
    queryKey: [...rootQueryKey, "day", selectedDate?.toFormat("yyyy-MM-dd")],
    queryFn: async () => {
      const forSelectedDayBody = constructScheduleRecommendationsBodyForDay({
        constraints,
        date: selectedDate,
        timezone: selectedTimezone,
      });

      if (
        !forSelectedDayBody ||
        !ownerVanityId ||
        !linkId ||
        isEmpty(forSelectedDayBody.recommendationWindows)
      ) {
        return EMPTY_TIME_SLOTS;
      }

      return api.scheduleRecommendations.listForPeriod(
        ownerVanityId,
        linkId,
        forSelectedDayBody,
      );
    },
    enabled: queriesEnabled && Boolean(selectedDate && constraints),
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const availabilityForMonthQuery = useQuery({
    queryKey: [...rootQueryKey, "month", selectedMonth?.toFormat("yyyy-MM-dd")],
    queryFn: async () => {
      const forSelectedMonthBody = constructScheduleRecommendationsBodyForMonth(
        {
          constraints,
          date: selectedMonth,
          timezone: selectedTimezone,
        },
      );

      if (
        !forSelectedMonthBody ||
        !ownerVanityId ||
        !linkId ||
        isEmpty(forSelectedMonthBody.recommendationWindows)
      ) {
        return EMPTY_AVAILABILITY;
      }

      return api.scheduleRecommendations.getAvailability(
        ownerVanityId,
        linkId,
        forSelectedMonthBody,
      );
    },
    enabled: queriesEnabled && Boolean(selectedMonth && constraints),
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const bestOptionsQuery = useQuery({
    queryKey: [...rootQueryKey, "bestOptions"],
    queryFn: async () => {
      const bestOptionsBody = constructBestOptionsBody({
        constraints,
        timezone: selectedTimezone,
        dateUtils: localizationContext.utils,
      });

      if (
        !bestOptionsBody ||
        !ownerVanityId ||
        !linkId ||
        isEmpty(bestOptionsBody.recommendationWindows)
      ) {
        return { thisWeek: [], nextWeek: [] };
      }

      return api.scheduleRecommendations.listBestOptions(
        ownerVanityId,
        linkId,
        bestOptionsBody,
      );
    },
    enabled: queriesEnabled && Boolean(constraints),
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });

  const hasError =
    scheduleRecommendationsForDayQuery.isError ||
    availabilityForMonthQuery.isError ||
    bestOptionsQuery.isError;

  useEffect(() => {
    if (!hasError) return;
    toast.error({
      type: "system",
      title: t`Error`,
      message: t`There was an error loading scheduling slots. Please refresh the page or try again later.`,
    });
  }, [hasError]);

  return {
    isAvailabilityLoading: availabilityForMonthQuery.isLoading,
    isAvailabilitySuccess: availabilityForMonthQuery.isSuccess,
    isBestOptionsLoading: bestOptionsQuery.isLoading,
    isTimeSlotsForDayLoading: scheduleRecommendationsForDayQuery.isLoading,
    isTimeSlotsForDaySuccess: scheduleRecommendationsForDayQuery.isSuccess,

    availability: availabilityForMonthQuery.data ?? EMPTY_AVAILABILITY,
    bestOptions: bestOptionsQuery.data ?? EMPTY_BEST_OPTIONS,
    timeSlotsForDay:
      scheduleRecommendationsForDayQuery.data ?? EMPTY_TIME_SLOTS,
  };
};

export default useAvailability;
